import { IMutation, IQuery } from '@app/@introspection';

/** The API has no subscriptions today, so the schema exports no ISubscription. */
type ISubscription = Record<string, unknown>;
import { DocumentTypes } from '@app/@introspection/documentTypes';
import { Injector } from '@rxdi/core';
import {
  ApolloClient,
  DataProxy,
  importQuery,
  MutationOptions,
  QueryOptions,
  SubscriptionOptions,
} from '@rxdi/graphql-client';
import { from, Observable } from 'rxjs';
import { switchMap } from 'rxjs/operators';

import { QueryBootstrapService } from '../services/bootstrap/query-bootstrap.service';

type Without<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;

interface ImportQueryMixin extends Without<QueryOptions, 'query'> {
  query: DocumentTypes;
}

interface ImportSubscriptionMixin extends Without<SubscriptionOptions, 'query'> {
  query: DocumentTypes;
}

interface ImportMutationMixin extends Without<MutationOptions, 'mutation'> {
  mutation: DocumentTypes;
  update?(proxy: DataProxy, res: { data: IMutation }): void;
}

export class BaseGraphqlLayer {
  @Injector(ApolloClient) public graphql: ApolloClient;
  @Injector(QueryBootstrapService) private bootstrap: QueryBootstrapService;

  /** Waits for the page-load bootstrap, so its cached data is found instead of fetched again. */
  query<T = IQuery>(options: ImportQueryMixin) {
    options.query = importQuery(options.query);
    return from(this.bootstrap.ready()).pipe(
      switchMap(() => from(this.graphql.query.bind(this.graphql)(options)))
    ) as Observable<{
      data: T;
    }>;
  }

  mutate<T = IMutation>(options: ImportMutationMixin) {
    options.mutation = importQuery(options.mutation);
    return from(this.graphql.mutate.bind(this.graphql)(options)) as Observable<{
      data: T;
    }>;
  }

  subscribe<T = ISubscription>(options: ImportSubscriptionMixin) {
    options.query = importQuery(options.query);
    return from(this.graphql.subscribe.bind(this.graphql)(options)) as Observable<{ data: T }>;
  }
}
